import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DropdownItem } from '../../models/filter.model';

import { TEXT_CONSTANTS } from '../../constants/text-constants';

@Component({
  selector: 'custom-dropdown',
  standalone: true,
  imports: [FormsModule, CommonModule],
  templateUrl: './custom-dropdown.component.html',
  styleUrl: './custom-dropdown.component.css'
})

export class CustomDropdownComponent<T = any> implements OnInit {
  TEXT = TEXT_CONSTANTS;
  @Input() items: DropdownItem<T>[] = [];
  @Input() multiple: boolean = false;
  @Input() placeholder: string = TEXT_CONSTANTS.COMMON.SELECT_PLACEHOLDER;
  @Input() searchable: boolean = false;
  @Input() disabled: boolean = false;
  @Input() hasError: boolean = false;
  @Input() errorMessage: string = '';
  @Input() maxSelectionDisplay: number = 2; // Máximo número de elementos a mostrar en el botón
  @Input() selectedValue: T | null = null; // Para selección simple
  @Input() selectedValues: T[] = []; // Para selección múltiple
  @Input() variant: 'standard' | 'text' = 'standard';
  @Input() customTextClasses: string = '';

  @Output() selectionChange = new EventEmitter<T | null>(); // Para selección simple
  @Output() multiSelectionChange = new EventEmitter<T[]>(); // Para selección múltiple

  isOpen: boolean = false;
  searchTerm: string = '';
  filteredItems: DropdownItem<T>[] = [];
  selectedItems: DropdownItem<T>[] = [];
  dropdownUp: boolean = false;
  dropdownFixedStyle: { [key: string]: string } = { top: '-9999px', left: '-9999px' };

  constructor(private elementRef: ElementRef) { }

  ngOnInit() {
    this.filteredItems = [...this.items];
    this.initializeSelection();
  }

  ngOnChanges() {
    this.filteredItems = [...this.items];
    this.initializeSelection();
    this.onSearch();
  }

  private initializeSelection() {
    if (this.multiple) {
      this.selectedItems = this.items.filter(item =>
        this.selectedValues.includes(item.value)
      );
    } else if (this.selectedValue !== null) {
      const selectedItem = this.items.find(item => item.value === this.selectedValue);
      this.selectedItems = selectedItem ? [selectedItem] : [];
    }
  }

  // Método público para actualizar la selección desde el componente padre
  updateSelectedItems() {
    if (this.multiple) {
      this.selectedItems = this.items.filter(item =>
        this.selectedValues.includes(item.value)
      );
    } else {
      const selectedItem = this.items.find(item => item.value === this.selectedValue);
      this.selectedItems = selectedItem ? [selectedItem] : [];
    }
  }

  toggleDropdown() {
    if (this.disabled) return;
    if (!this.isOpen) {
      this.computeDropdownPosition();
      this.isOpen = true;
    } else {
      this.isOpen = false;
    }
  }

  private computeDropdownPosition() {
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    this.dropdownUp = spaceBelow < 200 && spaceAbove > spaceBelow;
    this.dropdownFixedStyle = {
      minWidth: `${rect.width}px`,
      left: `${rect.left}px`,
      ...(this.dropdownUp
        ? { bottom: `${window.innerHeight - rect.top + 4}px`, top: 'auto' }
        : { top: `${rect.bottom + 4}px`, bottom: 'auto' }
      )
    };
  }

  onItemClick(item: DropdownItem<T>) {
    if (item.disabled) return;

    if (this.multiple) {
      this.handleMultipleSelection(item);
    } else {
      this.handleSingleSelection(item);
    }
  }

  private handleSingleSelection(item: DropdownItem<T>) {
    this.selectedItems = [item];
    this.selectedValue = item.value;
    this.selectionChange.emit(item.value);
    this.isOpen = false;
    // Limpiar el término de búsqueda después de la selección
    this.searchTerm = '';
    this.onSearch();
  }

  private handleMultipleSelection(item: DropdownItem<T>) {
    const index = this.selectedItems.findIndex(selected => selected.value === item.value);

    if (index > -1) {
      this.selectedItems.splice(index, 1);
    } else {
      this.selectedItems.push(item);
    }

    this.selectedValues = this.selectedItems.map(item => item.value);
    this.multiSelectionChange.emit(this.selectedValues);
  }

  isSelected(item: DropdownItem<T>): boolean {
    return this.selectedItems.some(selected => selected.value === item.value);
  }

  clearAll() {
    this.selectedItems = [];
    this.selectedValues = [];
    this.multiSelectionChange.emit(this.selectedValues);
  }

  // Nuevos métodos para manejar "Seleccionar todo"
  isAllSelected(): boolean {
    if (this.filteredItems.length === 0) return false;
    const selectableItems = this.filteredItems.filter(item => !item.disabled);
    return selectableItems.length > 0 &&
      selectableItems.every(item => this.isSelected(item));
  }

  isPartiallySelected(): boolean {
    if (this.filteredItems.length === 0) return false;
    const selectableItems = this.filteredItems.filter(item => !item.disabled);
    const selectedCount = selectableItems.filter(item => this.isSelected(item)).length;
    return selectedCount > 0 && selectedCount < selectableItems.length;
  }

  toggleSelectAll(event?: Event) {
    // Prevenir que el evento se propague y cierre el dropdown
    if (event) {
      event.stopPropagation();
    }

    const selectableItems = this.filteredItems.filter(item => !item.disabled);

    if (this.isAllSelected()) {
      // Deseleccionar todos los elementos visibles/filtrados
      selectableItems.forEach(item => {
        const index = this.selectedItems.findIndex(selected => selected.value === item.value);
        if (index > -1) {
          this.selectedItems.splice(index, 1);
        }
      });
    } else {
      // Seleccionar todos los elementos visibles/filtrados que no estén ya seleccionados
      selectableItems.forEach(item => {
        if (!this.isSelected(item)) {
          this.selectedItems.push(item);
        }
      });
    }

    this.selectedValues = this.selectedItems.map(item => item.value);
    this.multiSelectionChange.emit(this.selectedValues);
  }

  onSearch() {
    if (!this.searchTerm.trim()) {
      this.filteredItems = [...this.items];
      return;
    }

    const term = this.searchTerm.toLowerCase();
    this.filteredItems = this.items.filter(item =>
      item.label.toLowerCase().includes(term)
    );
  }

  getDisplayText(): string {
    if (this.selectedItems.length === 0) {
      return this.placeholder;
    }

    if (!this.multiple || this.selectedItems.length === 1) {
      return this.selectedItems[0].label;
    }

    if (this.selectedItems.length > 1) {
      return this.selectedItems.length.toString() + ' ' +
        (this.selectedItems.length > 1 ? TEXT_CONSTANTS.COMMON.SELECTED_PLURAL : TEXT_CONSTANTS.COMMON.SELECTED);
    }
    return '';
  }

  trackByFn(index: number, item: DropdownItem<T>): any {
    return item.value;
  }

  // Nuevos métodos para manejar el input principal
  getInputValue(): string {
    // Si estamos buscando, mostrar el término de búsqueda
    if (this.searchTerm && this.isOpen) {
      return this.searchTerm;
    }
    // Si hay selección, mostrar el item seleccionado
    if (this.selectedItems.length > 0) {
      return this.getDisplayText();
    }
    // Si no hay selección, mostrar vacío
    return '';
  }

  onInputChange(value: string) {
    this.searchTerm = value;
    this.onSearch();
  }

  onInputFocus() {
    this.computeDropdownPosition();
    this.isOpen = true;
    if (this.selectedItems.length > 0) {
      this.searchTerm = '';
      this.onSearch();
    }
  }

  onInputBlur() {
    // Delay para permitir clicks en el dropdown
    setTimeout(() => {
      if (!this.elementRef.nativeElement.contains(document.activeElement)) {
        this.isOpen = false;
        // Limpiar el término de búsqueda si no hay selección
        if (this.selectedItems.length === 0) {
          this.searchTerm = '';
        }
      }
    }, 150);
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      // Si hay elementos filtrados, seleccionar el primero
      if (this.filteredItems.length > 0 && !this.filteredItems[0].disabled) {
        this.onItemClick(this.filteredItems[0]);
      }
    } else if (event.key === 'Escape') {
      this.isOpen = false;
      this.searchTerm = '';
      this.onSearch();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  @HostListener('keydown.escape')
  onEscapeKey() {
    this.isOpen = false;
  }
}